import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { UserCustomProps } from '@hotmart/space-lib/dist/components/productFruits';
import { setProductFruitsUserEventListener } from '@hotmart/space-lib-events/dist/productFruitsUser';
import { useFeatureFlag } from 'src/hook/useFeatureFlag';

import type { ProductFruitsContextProps, ProductFruitsProviderProps } from './productFruits.types';
import { PRODUCT_FRUITS_WORKSPACE_CODE } from './productFruits.constants';
import { ProductFruitsComponent, useProductFruits } from './components/productFruits';

const ProductFruitsContext = createContext<ProductFruitsContextProps>(
  {} as ProductFruitsContextProps
);

export const ProductFruitsProvider = ({
  children,
  userCustomProps,
  token
}: ProductFruitsProviderProps) => {
  const enableProductFruitsFF = useFeatureFlag('enable_product_fruits');
  const mounted = useRef(false);
  const [userProps, setUserProps] = useState<UserCustomProps>(userCustomProps);

  const [productFruitsApi, setProductFruitsApi] =
    useState<ProductFruitsContextProps['productFruitsApi']>(null);

  useProductFruits((api: ProductFruitsContextProps['productFruitsApi']) => {
    mounted.current && setProductFruitsApi(api);
  });

  useEffect(() => {
    mounted.current = true;

    return () => {
      setProductFruitsApi(null);
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setProductFruitsUserEventListener((event) => {
      const payload = (event as CustomEvent<UserCustomProps>)?.detail;

      if (payload) {
        setUserProps((prev: UserCustomProps) => {
          const newUserMeta = { ...prev };

          for (const key in payload) {
            newUserMeta[key] = payload[key];
          }

          return newUserMeta;
        });
      }
    });
  }, []);

  if (!enableProductFruitsFF) {
    return <>{children}</>;
  }

  return (
    <ProductFruitsContext.Provider value={{ productFruitsApi, setUserCustomProps: setUserProps }}>
      <ProductFruitsComponent
        productFruitsWorkspaceCode={PRODUCT_FRUITS_WORKSPACE_CODE}
        userToken={token}
        userCustomProps={userProps}
      />
      {children}
    </ProductFruitsContext.Provider>
  );
};

export const useProductFruitsContext = () => {
  const context = useContext(ProductFruitsContext);

  if (!context) {
    throw new Error('Context must be used within the ProductFruitsContext.Provider');
  }

  return context;
};
